import { Trans, useLingui } from '@lingui/react/macro'

import { usePlansContext } from '../context'

import type { TUsePlanProductList } from '@/contexts/PlanContext/hooks/planProductList'
import type { EProductType, TProduct } from '@/contexts/PlanContext/type'

import { EDuration, EPaidProductType } from '@/contexts/PlanContext/type'
import { useSelectPackage } from '@/contexts/PlanContext/hooks/selectPackage'
import { getCurrentPrice } from '@/contexts/PlanContext/helpers/getCurrentPrice'


import { useMemo, useEffect } from 'react';
export interface IPlanCardInfoProps {
  productInfo: TUsePlanProductList['allProductsList'][number]
}

export function useProductName({
  type,
  product,
}: {
  type: EProductType | EPaidProductType
  product?: Pick<TProduct, 'productName'>
}) {
  const productName = useMemo(() => {
    const lowerProductName = product?.productName?.toLowerCase() || ''

    // 这里的标题文案不是简单复用 type：
    // 同属 BASIC 大类时，还要把 plus 单独展示成 Basic，而 standard 展示成 Standard。
    if (lowerProductName.includes('plus')) {
      return <Trans>Basic</Trans>
    }
    if (type === EPaidProductType.BASIC) {
      return <Trans>Standard</Trans>
    }
    if (type === EPaidProductType.PRO) {
      return <Trans>Pro</Trans>
    }
    if (type === EPaidProductType.UNLIMITED) {
      return <Trans>Unlimited</Trans>
    }
  }, [product?.productName, type])
  return productName
}

export function usePlanCardInfo({ productInfo }: IPlanCardInfoProps) {
  const { duration, type } = productInfo
  const { t } = useLingui()

  const {
    allProductsListMap,
    durationInfo,
    maxOutletsInfo,
    payStepInfo,
    isActive,
    plan,
    appName,
    isAnonymousPayment,
  } = usePlansContext()

  const { onChangeMaxOutlets } = maxOutletsInfo
  const isPro = type === EPaidProductType.PRO

  // 默认套餐卡标题按“卡片聚合类型”展示，不跟随滑块当前选中的具体 SKU 变化。
  const productName = useProductName({ type })

  const { selectedCreditsPackage, outletsText } = useSelectPackage({
    appName,
    plan,
    allProductsListMap,
    productInfo,
    durationInfo,
    isActive,
    payStepInfo,
    isAnonymousPayment,
  })

  const { curDuration } = durationInfo
  const isActiveDuration = curDuration === duration

  const priceCredits = useMemo(() => {
    return getCurrentPrice({
      productPrice: selectedCreditsPackage,
    })
  }, [selectedCreditsPackage])

  const priceMonthText = useMemo(() => {
    const total = priceCredits.currencyPriceMonth
    return `${total}`
  }, [priceCredits])

  const priceSuffixText = t`monthly`

  const renderOutletsText = useMemo(() => {
    if (isPro && duration === EDuration.YEARLY) {
      return outletsText
    }
    return null
  }, [duration, isPro, outletsText])

  useEffect(() => {
    if (renderOutletsText) {
      onChangeMaxOutlets(renderOutletsText)
    }
  }, [onChangeMaxOutlets, renderOutletsText])

  return {
    isActiveDuration,
    priceMonthText,
    priceSuffixText,
    appName,
    selectedCreditsPackage,
    productName,
    payStepInfo,
  }
}

export type TUsePlanCardInfo = ReturnType<typeof usePlanCardInfo>
